"use client";

import { createContext, useContext, useState, useCallback } from "react";
import { Paywall } from "@/components/Paywall";

type UpgradeOption = "pro" | "elite" | null;

interface PaywallContextType {
  paywallOpen: boolean;
  openPaywall: (option?: UpgradeOption) => void;
  closePaywall: () => void;
  selectedUpgradeOption: UpgradeOption;
}

const PaywallContext = createContext<PaywallContextType | undefined>(undefined);

export function PaywallProvider({ children }: { children: React.ReactNode }) {
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [selectedUpgradeOption, setSelectedUpgradeOption] = useState<UpgradeOption>(null);

  const openPaywall = useCallback((option: UpgradeOption = null) => {
    setSelectedUpgradeOption(option);
    setPaywallOpen(true);
  }, []);

  const closePaywall = useCallback(() => {
    setPaywallOpen(false);
    // Clear selected option after a short delay to allow animations
    setTimeout(() => {
      setSelectedUpgradeOption(null);
    }, 300);
  }, []);

  const handleUpgradePro = useCallback(() => {
    // TODO: Implement Pro upgrade logic
    console.log("Upgrade to Pro clicked");
    closePaywall();
    // Navigate to pricing or checkout page
  }, [closePaywall]);

  const handleUpgradeElite = useCallback(() => {
    // TODO: Implement Elite upgrade logic
    console.log("Upgrade to Elite clicked");
    closePaywall();
    // Navigate to pricing or checkout page
  }, [closePaywall]);

  return (
    <PaywallContext.Provider
      value={{
        paywallOpen,
        openPaywall,
        closePaywall,
        selectedUpgradeOption,
      }}
    >
      {children}
      <Paywall
        isOpen={paywallOpen}
        onClose={closePaywall}
        onUpgradePro={handleUpgradePro}
        onUpgradeElite={handleUpgradeElite}
      />
    </PaywallContext.Provider>
  );
}

export function usePaywall() {
  const context = useContext(PaywallContext);
  if (context === undefined) {
    // Fail-safe to avoid hard-crashing route-level UI if provider wiring regresses.
    // We still surface a console warning so the provider mismatch is visible.
    if (typeof window !== "undefined") {
      console.warn("usePaywall used outside PaywallProvider; falling back to no-op handlers.");
    }
    return {
      paywallOpen: false,
      openPaywall: () => {},
      closePaywall: () => {},
      selectedUpgradeOption: null,
    };
  }
  return context;
}

